import { useState, type FormEvent } from 'react';
import {
  useProjectMembers,
  useUpsertProjectMember,
  useRemoveProjectMember,
} from '../api/queries/project-members';
import { useAssignableUsers } from '../api/queries/project-tasks';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import './ProjectMembersSection.css';

interface ProjectMembersSectionProps {
  projectId: number;
}

export function ProjectMembersSection({ projectId }: ProjectMembersSectionProps): JSX.Element | null {
  const membersQuery = useProjectMembers(projectId);
  const upsert = useUpsertProjectMember();
  const remove = useRemoveProjectMember();
  const usersQuery = useAssignableUsers();

  const canManage = can('settings.manage_rbac');

  const [showAdd, setShowAdd] = useState(false);
  const [newUserId, setNewUserId] = useState<number | ''>('');
  const [newGrants, setNewGrants] = useState('');
  const [newDenies, setNewDenies] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (membersQuery.isPending) return null;

  const members = membersQuery.data?.project_members ?? [];
  const users = usersQuery.data?.users ?? [];

  const onAdd = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newUserId === '') return;
    try {
      await upsert.mutateAsync({
        project_id: projectId,
        user_id: newUserId,
        permissions: newGrants
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s.length > 0),
        denied_permissions: newDenies
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s.length > 0),
      });
      setShowAdd(false);
      setNewUserId('');
      setNewGrants('');
      setNewDenies('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError("You don't have permission to manage RBAC.");
        return;
      }
      if (err instanceof ApiError && err.status === 400) {
        setError('Invalid permission name in grants or denies.');
        return;
      }
      setError('Could not save member. Try again.');
    }
  };

  return (
    <section className="pms-block">
      <header className="pms-header">
        <h3>Project Members</h3>
        <span className="pms-meta">
          {members.length} member{members.length === 1 ? '' : 's'}
          {' · per-project permission overrides'}
        </span>
        {canManage && !showAdd && (
          <button
            type="button"
            className="btn-cyan-primary pms-add-btn"
            onClick={() => setShowAdd(true)}
          >
            Add member
          </button>
        )}
      </header>

      {showAdd && (
        <form className="pms-form" onSubmit={onAdd}>
          <label className="pms-field">
            <span>User</span>
            <select
              className="form-input"
              value={newUserId}
              onChange={(e) => {
                const v = e.target.value;
                setNewUserId(v === '' ? '' : Number(v));
              }}
              required
              disabled={upsert.isPending}
            >
              <option value="">Choose a user…</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.display_name}</option>
              ))}
            </select>
          </label>
          <label className="pms-field">
            <span>Granted permissions (comma-separated)</span>
            <input
              type="text"
              className="form-input"
              value={newGrants}
              onChange={(e) => setNewGrants(e.target.value)}
              placeholder="e.g. evidence.write, time_entry.approve"
              disabled={upsert.isPending}
            />
          </label>
          <label className="pms-field">
            <span>Denied permissions (comma-separated)</span>
            <input
              type="text"
              className="form-input"
              value={newDenies}
              onChange={(e) => setNewDenies(e.target.value)}
              placeholder="e.g. invoice.read"
              disabled={upsert.isPending}
            />
          </label>
          {error && <p className="pms-error" role="alert">{error}</p>}
          <div className="pms-form-actions">
            <button
              type="button"
              className="btn-charcoal-secondary"
              onClick={() => {
                setShowAdd(false);
                setError(null);
              }}
              disabled={upsert.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-cyan-primary"
              disabled={upsert.isPending || newUserId === ''}
            >
              {upsert.isPending ? 'Saving…' : 'Save member'}
            </button>
          </div>
        </form>
      )}

      {members.length === 0 ? (
        <p className="pms-empty">
          No per-project members. Project access falls back to the user's
          global role grants.
        </p>
      ) : (
        <ul className="pms-list">
          {members.map((m) => (
            <li key={m.id} className="pms-row">
              <div className="pms-row-main">
                <div className="pms-row-name">
                  {m.user_display_name ?? `User #${m.user_id}`}
                </div>
                <div className="pms-row-email">{m.user_email}</div>
                <div className="pms-row-perms">
                  {m.permissions.length > 0 && (
                    <span className="pms-grants">
                      <span className="pms-perm-label">Grants:</span>
                      {m.permissions.map((p) => (
                        <code key={p} className="pms-perm-chip pms-perm-grant">{p}</code>
                      ))}
                    </span>
                  )}
                  {m.denied_permissions.length > 0 && (
                    <span className="pms-denies">
                      <span className="pms-perm-label">Denies:</span>
                      {m.denied_permissions.map((p) => (
                        <code key={p} className="pms-perm-chip pms-perm-deny">{p}</code>
                      ))}
                    </span>
                  )}
                  {m.permissions.length === 0 && m.denied_permissions.length === 0 && (
                    <span className="pms-row-default">role grants only</span>
                  )}
                </div>
              </div>
              {canManage && (
                <button
                  type="button"
                  className="pms-row-remove"
                  onClick={() => {
                    if (confirm(`Remove ${m.user_display_name ?? 'this user'} from the project?`)) {
                      remove.mutate({ project_id: projectId, member_id: m.id });
                    }
                  }}
                  disabled={remove.isPending}
                  aria-label="Remove member"
                  title="Remove member"
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
